import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableHead, TableRow, TableCell, TableBody, Button, Stack, Chip } from '@mui/material';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiServices';
import { ReportPage, ReportCard, ExportMenu, LoadingRow, EmptyRow } from './ReportKit';
import PageHeader from '../common/PageHeader';
import dateFormats from '../../services/dateFormats';
import { printReport, textCol, currencyCol, dateCol } from '../../utils/pdfPrint';
import SendReportEmailDialog from '../common/SendReportEmailDialog';

function fmt(n) {
  return new Intl.NumberFormat('sq-AL', { style: 'currency', currency: 'EUR' }).format(n || 0);
}

export default function InspektoratReport() {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    apiService().query('getInspektoratWorkers', '').then(r => {
      if (r?.success) setWorkers(r.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const exportCsv = () => {
    const headers = [t('report.nr'), t('report.firstName'), t('report.lastName'), 'NID', t('report.hireDate'), t('report.position'), t('report.department'), t('report.contractType'), t('report.grossSalary'), t('report.contractStart'), t('report.contractEnd'), t('report.atkRegistered')];
    const lines = [headers.join(',')];
    workers.forEach((w, i) => {
      const row = [
        i+1, w.FirstName, w.LastName, w.NID, w.HireDate, w.Position,
        w.Department, w.ContractType, w.Salary, w.ContractStart, w.ContractEnd,
        w.ATKRegistered ? t('report.yes') : t('report.no'),
      ].map(v => `"${String(v ?? '').replace(/"/g, '""')}"`);
      lines.push(row.join(','));
    });
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `inspektorat_punetore_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  const exportPdf = () => {
    const pdfRows = workers.map((w, i) => ({
      ...w,
      _nr: i + 1,
      _name: w.FullName || `${w.FirstName || ''} ${w.LastName || ''}`.trim(),
      _atk: w.ATKRegistered ? '✓ Po' : '✗ Jo',
      _contractEnd: w.ContractEnd || t('report.noExpiry'),
    }));
    printReport({
      title: t('reports.inspektorat'),
      subtitle: t('report.inspektoratSubtitle'),
      orientation: 'landscape',
      columns: [
        { label: t('report.nr'), key: '_nr', align: 'right', width: '4%' },
        textCol(t('report.fullName'), '_name', '20%'),
        textCol('NID', 'NID', '12%'),
        dateCol(t('report.hireDate'), 'HireDate', '9%'),
        textCol(t('report.position'), 'Position', '14%'),
        textCol(t('report.department'), 'Department', '12%'),
        textCol(t('report.contractType'), 'ContractType', '9%'),
        currencyCol(t('report.grossSalary'), 'Salary', '10%'),
        textCol(t('report.contractEnd'), '_contractEnd', '9%'),
        { label: 'ATK', key: '_atk', align: 'center', width: '7%' },
      ],
      rows: pdfRows,
      badge: { label: `${workers.length} ${t('report.activeWorkers')}` },
      extraInfo: [t('report.officialDocument')],
    });
  };

  return (
    <ReportPage>
      <PageHeader
        title={t('reports.inspektorat')}
        subtitle={t('report.inspektoratAlert', { count: workers.length })}
        actions={
          <Stack direction="row" spacing={1}>
            <Button component={Link} to="/home/reports" size="small" variant="outlined" color="inherit" sx={{ textTransform: 'none' }}>{t('report.backLabel')}</Button>
            <ExportMenu onCsv={exportCsv} onPdf={exportPdf} onEmail={() => setEmailOpen(true)} disabled={workers.length === 0} />
          </Stack>
        }
      />

      <ReportCard count={workers.length} countLabel={t('report.workers')}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t('report.nr')}</TableCell>
              <TableCell>{t('report.fullName')}</TableCell>
              <TableCell>NID</TableCell>
              <TableCell>{t('report.hireDate')}</TableCell>
              <TableCell>{t('report.position')}</TableCell>
              <TableCell>{t('report.department')}</TableCell>
              <TableCell>{t('report.contractType')}</TableCell>
              <TableCell className="num">{t('report.grossSalary')}</TableCell>
              <TableCell>{t('report.contractStart')}</TableCell>
              <TableCell>{t('report.contractEnd')}</TableCell>
              <TableCell>ATK</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <LoadingRow colSpan={11} />
            ) : workers.length === 0 ? (
              <EmptyRow colSpan={11} />
            ) : workers.map((w, i) => (
              <TableRow key={w.Id} hover>
                <TableCell sx={{ color: 'text.disabled' }}>{i + 1}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{w.FullName}</TableCell>
                <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>{w.NID || '—'}</TableCell>
                <TableCell>{dateFormats(w.HireDate) || '—'}</TableCell>
                <TableCell>{w.Position || '—'}</TableCell>
                <TableCell>{w.Department || '—'}</TableCell>
                <TableCell>
                  <Chip size="small" label={w.ContractType} color={w.ContractType === 'Me Afat' ? 'warning' : 'success'} variant="outlined" />
                </TableCell>
                <TableCell className="num">{fmt(w.Salary)}</TableCell>
                <TableCell>{dateFormats(w.ContractStart) || '—'}</TableCell>
                <TableCell>{w.ContractEnd ? dateFormats(w.ContractEnd) : <Chip size="small" label={t('report.noExpiry')} color="success" variant="outlined" />}</TableCell>
                <TableCell>
                  {w.ATKRegistered
                    ? <Chip size="small" color="success" variant="outlined" label={t('report.yes')} />
                    : <Chip size="small" color="error"   variant="outlined" label={t('report.no')} />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ReportCard>

      <SendReportEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        reportTitle={t('reports.inspektorat')}
        subtitle={`${t('report.generated')}: ${new Date().toLocaleDateString('sq-AL')}`}
        columns={[
          { label: t('report.nr'), key: (r, i) => i + 1, align: 'right' },
          { label: t('report.fullName'), key: r => r.FullName || `${r.FirstName || ''} ${r.LastName || ''}`.trim(), align: 'left' },
          { label: 'NID', key: 'NID', align: 'left' },
          { label: t('report.hireDate'), key: 'HireDate', align: 'left', format: 'date' },
          { label: t('report.position'), key: 'Position', align: 'left' },
          { label: t('report.department'), key: 'Department', align: 'left' },
          { label: t('report.grossSalary'), key: 'Salary', align: 'right', format: 'currency' },
        ]}
        rows={workers.map((w, i) => ({ ...w, _nr: i + 1 }))}
        workers={workers}
      />
    </ReportPage>
  );
}
